import { useEffect, useCallback, useState } from 'react';
import wsService from '../services/websocket';

/**
 * Hook para usar notificações WebSocket
 * 
 * @example
 * const { isConnected, lastMessage } = useWebSocket({
 *   onChatMessage: (data) => console.log('Nova mensagem:', data),
 *   onDocument: (data) => console.log('Novo documento:', data),
 *   onHorario: (data) => console.log('Novo horário:', data),
 *   onTarefa: (data) => console.log('Nova tarefa:', data),
 *   onObjetivo: (data) => console.log('Objetivo atingido:', data),
 *   onUpdateCounters: () => recarregarContadores()
 * });
 */
export function useWebSocket(handlers = {}) {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);

  useEffect(() => {
    // Conectar WebSocket
    wsService.connect();

    // Listeners de estado
    const unsubConnected = wsService.on('connected', () => setIsConnected(true));
    const unsubDisconnected = wsService.on('disconnected', () => setIsConnected(false));

    // Listener genérico
    const unsubMessage = wsService.on('message', (data) => {
      setLastMessage(data);
    });

    // Listeners específicos
    const unsubscribers = [];

    if (handlers.onChatMessage) {
      unsubscribers.push(wsService.on('chat_message', handlers.onChatMessage));
    }

    if (handlers.onDocument) {
      unsubscribers.push(wsService.on('document', handlers.onDocument));
    }

    if (handlers.onDocumentAdmin) {
      unsubscribers.push(wsService.on('document_admin', handlers.onDocumentAdmin));
    }

    if (handlers.onHorario) {
      unsubscribers.push(wsService.on('horario', handlers.onHorario));
    }

    if (handlers.onTarefa) {
      unsubscribers.push(wsService.on('tarefa', handlers.onTarefa));
    }

    if (handlers.onObjetivo) {
      unsubscribers.push(wsService.on('objetivo', handlers.onObjetivo));
    }

    if (handlers.onObjetivoAdmin) {
      unsubscribers.push(wsService.on('objetivo_admin', handlers.onObjetivoAdmin));
    }

    if (handlers.onUpdateCounters) {
      unsubscribers.push(wsService.on('update_counters', handlers.onUpdateCounters));
    }

    // Cleanup
    return () => {
      unsubConnected();
      unsubDisconnected();
      unsubMessage();
      unsubscribers.forEach(unsub => unsub());
    };
  }, [handlers]);

  const disconnect = useCallback(() => {
    wsService.disconnect();
  }, []);

  const reconnect = useCallback(() => {
    wsService.disconnect();
    wsService.connect();
  }, []);

  return {
    isConnected,
    lastMessage,
    disconnect,
    reconnect
  };
}

export default useWebSocket;
